<template>
  <div style="white-space: nowrap">
    <b-overlay :show="isLoading || isModalLoading" class="d-flex" style="gap: 12px">
      <!-- Seitenleiste -->
      <div>
        <div class="card p-2" style="gap: 12px; align-items: center; width: 100%">
          <div
            v-for="(saison, i) in sortSaisons"
            :key="'saison-' + saison.id"
            class="p-3 rounded"
            style="cursor: pointer; width: 100%"
            :class="activeSaisonGueltigAb === saison.gueltigAb ? 'bg-primary text-white' : 'bg-secondary'"
            @click="setActiveSaisonGueltigAb(saison.gueltigAb)"
          >
            <div class="d-flex">
              {{ `${i + 1}. Saison` }}
            </div>

            <div>{{ 'Ab ' + saison.formattedGueltigAb }}</div>
          </div>
          <div class="w-100" style="display: grid; grid-template-columns: repeat(5, 1fr)">
            <b-button
              class="btn-xs btn py-1 px-2 justify-content-center align-items-center d-flex"
              v-b-tooltip.hover
              :title="'Die Passdaten werden pro Reise und pro Saison angelegt und gepflegt. Die Konfiguration gibt vor, welche Passdaten und Dokumente für die Reise bzw. den FOM notwendig sind.'"
            >
              <i class="fas fa-info m-0 p-0"></i>
            </b-button>
            <div style="grid-column-start: 3" class="bg-primary plus-sign" v-b-modal.modalSaisonHinzufuegen>
              +
            </div>
          </div>
        </div>
      </div>

      <!-- Show active season -->
      <div class="d-flex w-100" style="flex-direction: column; position: relative">
        <div class="card passdaten-konfiguration">
          <span
            class="d-flex ml-auto"
            style="
              gap: 12px;
              width: fit-content;
              height: 38px;
              position: sticky;
              top: 130px;
              z-index: 99;
              background-color: #ffffff;
              margin-bottom: -30px;
            "
          >
            <b-button
              size="sm"
              class="save-btn dialog-btn"
              @click="saveChanges"
              :disabled="disableSichernButton"
              :variant="disableSichernButton ? '' : 'primary'"
            >
              Speichern
            </b-button>
            <b-button
              size="sm"
              class="dialog-btn-secondary"
              v-b-modal.modalSaisonLöschen
              variant="secondary"
              v-if="canRemoveSaison"
            >
              Saison löschen
            </b-button>
          </span>

          <h4><p>Passdaten-Konfiguration</p></h4>

          <!-- die einzelnen Kategorien der Konfiguration, zweispaltig angeordnet -->
          <div class="passdaten-konfiguration__grid">
            <PassdatenKonfigurationSection
              v-for="kategorie in kategorien"
              :key="kategorie.titel"
              :kategorie="kategorie"
              :form="form"
              :originalForm="originalForm"
              :domEditable="DOMAllowedToEdit"
              :fomEditable="FOMAllowedToEdit"
              @input="onFeldInput"
            />
          </div>
        </div>
      </div>
    </b-overlay>

    <b-modal id="modalSaisonHinzufuegen" centered title="Saison hinzufügen" size="sm">
      <b-overlay :show="isModalLoading">
        <b-form-group label="Gültig ab">
          <DatePicker :lang="langConfig" type="date" format="DD.MM.YYYY" v-model="gueltigAb"></DatePicker>
        </b-form-group>
      </b-overlay>
      <template #modal-footer="{ hide }" class="pl-1">
        <b-button class="dialog-btn-secondary" @click="resetAndCloseModal(hide)">Abbrechen</b-button>
        <b-button
          class="dialog-btn"
          @click="addNewSaison(hide)"
          :disabled="gueltigAb === null"
          variant="primary"
        >
          Erstellen
        </b-button>
      </template>
    </b-modal>

    <DeleteConfirmationModal
      id="modalSaisonLöschen"
      title="Saison löschen?"
      entityBezeichnung="die Saison"
      :label="activeSaisonGueltigAbFormatted"
      :loading="isModalLoading"
      @confirm="deleteSaison"
    />
  </div>
</template>

<script>
import { isSameDay, compareDesc, format } from 'date-fns';
import apiService from '@/core/common/services/api.service';
import { langConfig, formatDateWithoutHours } from '@/core/common/helpers/utils.js';
import DatePicker from 'vue2-datepicker';
import { mapGetters } from 'vuex';
import DeleteConfirmationModal from '@/components/common/delete-confirmation-modal.vue';
import PassdatenKonfigurationSection from './passdaten-konfiguration/passdaten-konfiguration-section.vue';
import { KATEGORIEN, ALLE_KEYS } from './passdaten-konfiguration/passdaten-konfiguration.felder.js';

export default {
  props: {
    reise: { type: Object },
    konfigurationen: {
      type: Array,
    },
  },
  components: {
    DatePicker,
    DeleteConfirmationModal,
    PassdatenKonfigurationSection,
  },
  data() {
    return {
      langConfig,
      gueltigAb: null,
      lastSaveChangedFields: [],
      preexistingFormDataMultipleReisetermin: null,

      saisons: [],
      activeSaisonGueltigAb: null,
      kategorien: KATEGORIEN,
      originalForm: {},
      form: {},
      isLoading: null,
      isModalLoading: false,
    };
  },
  mounted() {
    this.activeSaisonGueltigAb = this.sortSaisons[0]?.gueltigAb;
    this.setEditedKonfiguration();
  },
  watch: {
    activeSaisonGueltigAb() {
      this.setEditedKonfiguration();
    },
  },
  computed: {
    ...mapGetters(['parsedRoles']),

    DOMAllowedToEdit() {
      return this.parsedRoles.includes('Admin') || this.parsedRoles.includes('Dom');
    },
    FOMAllowedToEdit() {
      return this.parsedRoles.includes('Admin') || this.parsedRoles.includes('FOM');
    },
    activeKonfiguration() {
      if (this.activeSaisonGueltigAb == null) {
        return null;
      }
      return this.konfigurationen.find(item => item.gueltigAb === this.activeSaisonGueltigAb);
    },
    sortSaisons() {
      return this.konfigurationen
        .map(item => {
          return { ...item, formattedGueltigAb: formatDateWithoutHours(item.gueltigAb) };
        })
        .sort((a, b) => compareDesc(new Date(a.gueltigAb), new Date(b.gueltigAb)));
    },
    activeSaisonGueltigAbFormatted() {
      return formatDateWithoutHours(this.activeSaisonGueltigAb);
    },
    changedFields() {
      return ALLE_KEYS.some(key => {
        const bearbeitet = this.form[key];
        const original = this.originalForm[key];
        if (Array.isArray(bearbeitet) || Array.isArray(original)) {
          return (
            JSON.stringify([...(bearbeitet || [])].sort()) !== JSON.stringify([...(original || [])].sort())
          );
        }
        return bearbeitet !== original;
      });
    },
    disableSichernButton() {
      return !this.changedFields;
    },
    canRemoveSaison() {
      return (
        !isSameDay(new Date(this.activeSaisonGueltigAb), new Date('0001-01-01')) &&
        this.konfigurationen?.length !== 1
      );
    },
  },
  methods: {
    formAusKonfiguration(konfiguration) {
      const form = ALLE_KEYS.reduce((werte, key) => {
        werte[key] = konfiguration[key];
        return werte;
      }, {});

      // Dringlichkeit nutzt 0 als "keine Angabe", damit die Einfachauswahl leerbar bleibt
      form.dringlichkeit = konfiguration.dringlichkeit ?? 0;
      form.validePassarten = [...(konfiguration.validePassarten ?? [])];
      form.einreichungsfristZumErstenReleasedatum = konfiguration.einreichungsfristZumErstenReleasedatum
        ? 'erstes'
        : 'letztes';

      return form;
    },
    // Überträgt das Formular-Modell zurück in die DTO-Struktur
    konfigurationAusForm(form) {
      const params = ALLE_KEYS.reduce((werte, key) => {
        werte[key] = form[key];
        return werte;
      }, {});

      params.dringlichkeit = form.dringlichkeit ? form.dringlichkeit : null;
      params.validePassarten = [...(form.validePassarten ?? [])];
      params.einreichungsfristZumErstenReleasedatum =
        form.einreichungsfristZumErstenReleasedatum === 'erstes';

      return params;
    },
    onFeldInput({ key, value }) {
      this.$set(this.form, key, value);
    },
    setEditedKonfiguration() {
      if (!this.activeKonfiguration) {
        return;
      }
      this.originalForm = this.formAusKonfiguration(this.activeKonfiguration);
      this.form = this.formAusKonfiguration(this.activeKonfiguration);
    },
    setActiveSaisonGueltigAb(gueltigAbDate) {
      this.activeSaisonGueltigAb = gueltigAbDate;
      this.setEditedKonfiguration();
    },
    saveChanges() {
      this.isLoading = true;
      const reisekuerzel = this.reise.reisekuerzel;

      const params = {
        ...this.konfigurationAusForm(this.form),
        id: this.activeKonfiguration.id,
        gueltigAb: this.activeKonfiguration.gueltigAb,
      };

      apiService
        .put(`Reise/${reisekuerzel}/konfiguration/${params.id}`, params)
        .then(response => {
          if (response.data.result.isSuccess && response.data.result.value != null) {
            const updatedData = response.data.result.value;
            this.toast('Erfolgreich bearbeitet.');

            // Weise die neuen Werte der upgedateten Saison zu
            this.konfigurationen = this.konfigurationen.map(saison => {
              if (updatedData.gueltigAb != saison.gueltigAb) return saison;

              return updatedData;
            });

            // Update die edited saison
            this.setEditedKonfiguration();
          }
        })
        .catch(error => {
          console.error('API-Fehler: ', error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    async deleteSaison() {
      if (!isSameDay(new Date(this.activeKonfiguration.gueltigAb), new Date('0001-01-01'))) {
        // Hole die ID der aktiven Konfiguration
        const { id: configId } = this.activeKonfiguration;

        const { reisekuerzel } = this.reise;
        this.isModalLoading = true;
        apiService.delete(`Reise/${reisekuerzel}/konfiguration/${configId}`).then(() => {
          this.toast('Erfolgreich gelöscht.');

          // Konfigurationen -> Lösche die Saison ( filtern die raus )
          this.konfigurationen = this.konfigurationen.filter(
            konfiguration => konfiguration.gueltigAb != this.activeKonfiguration.gueltigAb
          );
          // Setze das aktive gültig ab auf die jetzt neuerdings oberste saison
          this.activeSaisonGueltigAb = this.sortSaisons[0]?.gueltigAb;
          this.setEditedKonfiguration();

          this.isModalLoading = false;
          this.isLoading = false;

          this.$bvModal.hide('modalSaisonLöschen');
        });
      }
    },
    addNewSaison(hide) {
      if (this.gueltigAb) {
        const { reisekuerzel } = this.reise;
        this.isModalLoading = true;
        const newFormattedGueltigAb = format(this.gueltigAb, 'yyyy-MM-dd');

        // Die neue Saison übernimmt den gespeicherten Stand der aktiven Saison
        const saisonParams = {
          ...this.konfigurationAusForm(this.formAusKonfiguration(this.activeKonfiguration)),
          gueltigAb: newFormattedGueltigAb,
        };

        apiService.post(`Reise/${reisekuerzel}/konfiguration`, saisonParams).then(response => {
          this.toast('Erfolgreich erstellt.');
          const newSaison = response.data.result.value;

          this.konfigurationen = [...this.konfigurationen, newSaison];
          this.setActiveSaisonGueltigAb(newSaison.gueltigAb);
          this.setEditedKonfiguration();

          this.isLoading = false;
          this.gueltigAb = null;
          this.isModalLoading = false;
          hide();
        });
      }
    },
    resetAndCloseModal(hide) {
      this.gueltigAb = null;
      hide();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/sass/components/_variables.bootstrap.scss';
.plus-sign {
  height: 24px;
  width: 24px;
  border-radius: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 20px;
  color: white;
  cursor: pointer;
  -webkit-user-select: none; /* Safari */
  -ms-user-select: none; /* IE 10 and IE 11 */
  user-select: none; /* Standard syntax */
}

.passdaten-konfiguration {
  width: 100%;
  padding: 12px;
}

:deep(.custom-checkbox-DOM .custom-control-input ~ .custom-control-label::before) {
  border-color: var(--success);
}
:deep(.custom-checkbox-DOM .custom-control-input:checked ~ .custom-control-label::before) {
  border-color: var(--success);
  background-color: var(--success);
}
:deep(.custom-checkbox-FOM .custom-control-input ~ .custom-control-label::before) {
  border-color: var(--warning);
}
:deep(.custom-checkbox-FOM .custom-control-input:checked ~ .custom-control-label::before) {
  border-color: var(--warning);
  background-color: var(--warning);
}

.passdaten-konfiguration__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
</style>
